import { AlertTriangle, ArrowLeftRight, Check, X } from 'lucide-react';
import { Input } from '../../../ui/input';
import { Textarea } from '../../../ui/textarea';

// Phase 3.6C.1: review + correct what the AI read BEFORE anything is drawn. Edits stay in the browser and are rebuilt
// deterministically on the server (no new AI call).
const STRUCTURES = [
  ['progression', 'Progression along a line'], ['cycle', 'Cycle'], ['hierarchy', 'Hierarchy'],
  ['iceberg', 'Iceberg (above / below a surface)'], ['flowchart', 'Flowchart'], ['framework', 'Framework / columns'],
  ['mindmap', 'Mind map'], ['list', 'List'], ['freeform', 'Freeform sketch'], ['other', 'Other'],
];
const PATH_KINDS = [
  ['progression', 'Progression line'], ['boundary', 'Boundary'], ['waterline', 'Waterline / wavy divider'],
  ['outline', 'Outline / curve'], ['divider', 'Divider'], ['connector', 'Connector'], ['other', 'Line (meaning unsure)'],
];
const ARROWS = [['none', 'No arrowhead'], ['end', 'Arrow at end'], ['start', 'Arrow at start'], ['both', 'Both ends']];
const SELECT = 'h-8 max-w-full min-w-0 rounded-md border border-input bg-background px-2 text-xs';

const Unsure = ({ onConfirm, testid }) => (
  <span className="inline-flex items-center gap-1 text-[11px] font-medium text-amber-800 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
    <AlertTriangle className="h-3 w-3" />Unsure
    <button type="button" onClick={onConfirm} data-testid={testid} className="ml-1 underline hover:text-amber-950">Looks right</button>
  </span>
);

const IconBtn = ({ onClick, label, testid, children }) => (
  <button type="button" onClick={onClick} aria-label={label} title={label} data-testid={testid}
    className="h-7 w-7 flex-shrink-0 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900 flex items-center justify-center">{children}</button>
);

function Section({ title, hint, testid, children }) {
  return (
    <section className="space-y-2" data-testid={testid}>
      <div>
        <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{title}</h4>
        {hint && <p className="text-[11px] text-muted-foreground">{hint}</p>}
      </div>
      {children}
    </section>
  );
}

function ConceptRow({ n, onChange, onRemove }) {
  return (
    <div className={`flex flex-wrap items-center gap-2 rounded-md border px-2 py-1.5 min-w-0 ${n.uncertain ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'}`} data-testid={`wb-interp-concept-${n.id}`}>
      <Input value={n.label} maxLength={60} className="h-8 text-sm flex-1 min-w-[8rem]" data-testid={`wb-interp-concept-input-${n.id}`}
        onChange={(e) => onChange({ label: e.target.value, uncertain: false })} />
      {n.uncertain && <Unsure onConfirm={() => onChange({ uncertain: false })} testid={`wb-interp-concept-confirm-${n.id}`} />}
      <IconBtn onClick={onRemove} label="Remove concept" testid={`wb-interp-concept-remove-${n.id}`}><X className="h-4 w-4" /></IconBtn>
    </div>
  );
}

function PathRow({ p, labelOf, onChange, onRemove }) {
  const near = p.through.map(labelOf).filter(Boolean).join(' → ');
  return (
    <div className={`space-y-1.5 rounded-md border px-2 py-2 min-w-0 ${p.uncertain ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'}`} data-testid={`wb-interp-path-${p.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="h-3 w-6 rounded-full flex-shrink-0" style={{ backgroundColor: p.color || '#475569' }} aria-hidden />
        <select value={p.kind} onChange={(e) => onChange({ kind: e.target.value })} className={SELECT} data-testid={`wb-interp-path-kind-${p.id}`}>
          {PATH_KINDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <select value={p.arrow} onChange={(e) => onChange({ arrow: e.target.value })} className={SELECT} data-testid={`wb-interp-path-arrow-${p.id}`}>
          {ARROWS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        {p.uncertain && <Unsure onConfirm={() => onChange({ uncertain: false })} testid={`wb-interp-path-confirm-${p.id}`} />}
        <span className="flex-1" />
        <IconBtn onClick={onRemove} label="Remove line" testid={`wb-interp-path-remove-${p.id}`}><X className="h-4 w-4" /></IconBtn>
      </div>
      {(p.meaning || near) && (
        <p className="text-[11px] text-slate-600 break-words">{p.meaning}{p.meaning && near ? ' · ' : ''}{near ? `passes near ${near}` : ''}</p>
      )}
    </div>
  );
}

function EdgeRow({ e, i, labelOf, onChange, onRemove }) {
  const sym = e.style === 'line' ? '—' : e.style === 'both' ? '↔' : '→';
  return (
    <div className={`flex items-center gap-2 rounded-md border px-2 py-1.5 text-sm min-w-0 ${e.uncertain ? 'border-amber-300 bg-amber-50/60' : 'border-slate-200'}`} data-testid={`wb-interp-edge-${i}`}>
      <span className="flex-1 min-w-0 truncate">{labelOf(e.from)} <b className="text-slate-500">{sym}</b> {labelOf(e.to)}{e.label ? ` (${e.label})` : ''}</span>
      {e.uncertain && <Unsure onConfirm={() => onChange({ uncertain: false })} testid={`wb-interp-edge-confirm-${i}`} />}
      <IconBtn onClick={() => onChange({ from: e.to, to: e.from, uncertain: false })} label="Reverse direction" testid={`wb-interp-edge-swap-${i}`}><ArrowLeftRight className="h-4 w-4" /></IconBtn>
      <IconBtn onClick={onRemove} label="Remove connection" testid={`wb-interp-edge-remove-${i}`}><X className="h-4 w-4" /></IconBtn>
    </div>
  );
}

function Uncertainties({ items, onResolve }) {
  if (!items.length) return <p className="text-xs text-muted-foreground" data-testid="wb-interp-no-uncertainties">Nothing flagged.</p>;
  return (
    <ul className="space-y-1" data-testid="wb-interp-uncertainties">
      {items.map((u, i) => (
        <li key={`${i}-${u}`} className="flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5" data-testid={`wb-interp-uncertainty-${i}`}>
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" /><span className="flex-1 min-w-0 break-words">{u}</span>
          <button type="button" onClick={() => onResolve(i)} className="inline-flex items-center gap-1 font-medium underline" data-testid={`wb-interp-uncertainty-resolve-${i}`}>
            <Check className="h-3 w-3" />Resolved
          </button>
        </li>
      ))}
    </ul>
  );
}

export function ModePicker({ mode, onChange, disabled }) {
  const opts = [['redesign', 'Professional Redesign', 'Polished and presentation-ready, keeping your concepts and lines.'],
    ['faithful', 'Faithful Reconstruction', 'As close as practical to the photo: positions, wording, lines.']];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" data-testid="wb-photo-mode">
      {opts.map(([v, l, d]) => (
        <button key={v} type="button" role="radio" aria-checked={mode === v} disabled={disabled} onClick={() => onChange(v)} data-testid={`wb-photo-mode-${v}`}
          className={`text-left rounded-md border px-3 py-2 transition-colors ${mode === v ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 hover:bg-slate-50'}`}>
          <span className="block text-sm font-medium">{l}</span>
          <span className={`block text-[11px] ${mode === v ? 'text-slate-200' : 'text-muted-foreground'}`}>{d}</span>
        </button>
      ))}
    </div>
  );
}

export function InterpretationReview({ value, onChange }) {
  const c = value;
  const set = (patch) => onChange({ ...c, ...patch });
  const labelOf = (id) => c.nodes.find((n) => n.id === id)?.label;
  const patchAt = (key, i, patch) => set({ [key]: c[key].map((x, k) => (k === i ? { ...x, ...patch } : x)) });
  const removeNode = (id) => set({
    nodes: c.nodes.filter((n) => n.id !== id),
    edges: c.edges.filter((e) => e.from !== id && e.to !== id),
    paths: c.paths.map((p) => ({ ...p, through: p.through.filter((t) => t !== id) })),
  });
  return (
    <div className="space-y-4 min-w-0" data-testid="wb-interp-review">
      <Section title="Title" hint="Only text actually written on the board.">
        <Input value={c.title} maxLength={100} placeholder="No title on the board" onChange={(e) => set({ title: e.target.value })} data-testid="wb-interp-title" />
      </Section>
      <Section title="What the drawing communicates" testid="wb-interp-structure">
        <select value={c.structure.type} onChange={(e) => set({ structure: { ...c.structure, type: e.target.value } })} className={`${SELECT} w-full`} data-testid="wb-interp-structure-type">
          {STRUCTURES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <Textarea rows={2} maxLength={240} value={c.structure.summary} className="text-sm" data-testid="wb-interp-structure-summary"
          placeholder="Describe what the drawing shows" onChange={(e) => set({ structure: { ...c.structure, summary: e.target.value } })} />
      </Section>
      <Section title={`Concepts (${c.nodes.length})`} hint="Fix any misread words. Editing a word marks it as checked." testid="wb-interp-concepts">
        {c.nodes.map((n) => (
          <ConceptRow key={n.id} n={n} onRemove={() => removeNode(n.id)}
            onChange={(patch) => set({ nodes: c.nodes.map((x) => (x.id === n.id ? { ...x, ...patch } : x)) })} />
        ))}
      </Section>
      <Section title="Lines, paths and connections" hint="Kept as drawn. A line passing near a word is not treated as crossing it out." testid="wb-interp-relationships">
        {c.paths.map((p, i) => <PathRow key={p.id} p={p} labelOf={labelOf} onChange={(patch) => patchAt('paths', i, patch)} onRemove={() => set({ paths: c.paths.filter((_, k) => k !== i) })} />)}
        {c.edges.map((e, i) => <EdgeRow key={`${e.from}-${e.to}-${i}`} e={e} i={i} labelOf={labelOf} onChange={(patch) => patchAt('edges', i, patch)} onRemove={() => set({ edges: c.edges.filter((_, k) => k !== i) })} />)}
        {!c.paths.length && !c.edges.length && <p className="text-xs text-muted-foreground" data-testid="wb-interp-no-lines">No lines or connections were found.</p>}
      </Section>
      <Section title="Uncertainties" hint="You don't have to answer these. Anything left unsure stays marked with ? on the board." testid="wb-interp-uncertainty-section">
        <Uncertainties items={c.uncertainties} onResolve={(i) => set({ uncertainties: c.uncertainties.filter((_, k) => k !== i) })} />
        {c.unclear?.length > 0 && (
          <p className="text-xs text-slate-600" data-testid="wb-interp-unclear">Could not read: {c.unclear.join('; ')}. Nothing was guessed for these.</p>
        )}
      </Section>
    </div>
  );
}
